import {
  bulletStyle,
  escapeHtml,
  paragraphStyle,
  runStyle,
  toCssText,
  type Paragraph,
  type TextRun,
  type TrainingDesign,
} from '@allemnii-pulse/shared';
import { normalizeRuns, runFormat, type RunFormat, type TextPos } from './richText.js';

/**
 * The bridge between a text element's model and the contentEditable used to edit it in place.
 * The editable's DOM is built from the model (buildEditableHtml), never the other way round:
 * after each input event the DOM is read back into paragraphs (parseEditable) and the DOM itself
 * is left alone, so the caret isn't disturbed while typing. Anything structural — Enter, paste,
 * toolbar formatting — is applied to the model and the DOM rebuilt, with the caret mapped
 * through model positions (domToPos / posToDom).
 *
 * Each paragraph is a <div data-para> carrying its alignment and bullet; each run a <span
 * data-run='{formatting}'>. The bullet glyph is a non-editable span the parser skips.
 */

/** Non-breaking and zero-width spaces the browser inserts while editing — built from char codes
 * so the source holds no invisible characters. */
const NBSP = String.fromCharCode(0xa0);
const ZWSP = String.fromCharCode(0x200b);

function cleanText(text: string): string {
  return text.split(NBSP).join(' ').split(ZWSP).join('');
}

function attr(text: string): string {
  return escapeHtml(text);
}

export function buildEditableHtml(paragraphs: Paragraph[], design: TrainingDesign): string {
  return paragraphs
    .map((p) => {
      const data = `data-para="1"${p.align ? ` data-align="${p.align}"` : ''}${p.bullet ? ' data-bullet-para="1"' : ''}`;
      const bullet = p.bullet
        ? `<span data-bullet="1" contenteditable="false" style="${attr(toCssText(bulletStyle(design)))}">•</span>`
        : '';
      const hasText = p.runs.some((r) => r.text);
      const runs = hasText
        ? p.runs
            .filter((r) => r.text)
            .map(
              (r) =>
                `<span data-run="${attr(JSON.stringify(runFormat(r)))}" style="${attr(toCssText(runStyle(r)))}">${escapeHtml(r.text)}</span>`,
            )
            .join('')
        : `<span data-run="${attr(JSON.stringify(runFormat(p.runs[0] ?? { text: '' })))}" style="${attr(toCssText(runStyle(p.runs[0] ?? { text: '' })))}"><br></span>`;
      return `<div ${data} style="${attr(toCssText(paragraphStyle(p)))}">${bullet}${runs}</div>`;
    })
    .join('');
}

function isBullet(node: Node): boolean {
  return node instanceof HTMLElement && node.dataset.bullet === '1';
}

function collectRuns(node: Node, inherited: RunFormat, out: TextRun[]) {
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = cleanText(child.textContent ?? '');
      if (text) out.push({ ...inherited, text });
      return;
    }
    if (!(child instanceof HTMLElement) || isBullet(child) || child.tagName === 'BR') return;
    let format: RunFormat = inherited;
    if (child.dataset.run) {
      try {
        format = { ...inherited, ...(JSON.parse(child.dataset.run) as RunFormat) };
      } catch {
        // A malformed attribute can only come from outside the editor — ignore its formatting.
      }
    }
    // The browser's own formatting tags, should any slip in (e.g. a spellcheck replacement).
    const tag = child.tagName;
    if (tag === 'B' || tag === 'STRONG') format = { ...format, bold: true };
    if (tag === 'I' || tag === 'EM') format = { ...format, italic: true };
    if (tag === 'U') format = { ...format, underline: true };
    collectRuns(child, format, out);
  });
}

function paragraphFromElement(el: HTMLElement, fallbackFormat: RunFormat): Paragraph {
  const runs: TextRun[] = [];
  collectRuns(el, {}, runs);
  const align = el.dataset.align as Paragraph['align'] | undefined;
  const emptyFormat = (() => {
    const span = el.querySelector<HTMLElement>('[data-run]');
    if (!span?.dataset.run) return fallbackFormat;
    try {
      return JSON.parse(span.dataset.run) as RunFormat;
    } catch {
      return fallbackFormat;
    }
  })();
  return {
    runs: normalizeRuns(runs.length ? runs : [{ ...emptyFormat, text: '' }]),
    ...(align ? { align } : {}),
    ...(el.dataset.bulletPara === '1' ? { bullet: true } : {}),
  };
}

/** Reads the editable's DOM back into paragraphs. Element children are paragraphs; stray text
 * nodes directly under the root (the browser can leave them) join the paragraph before them. */
export function parseEditable(root: HTMLElement): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  root.childNodes.forEach((child) => {
    if (child instanceof HTMLElement) {
      paragraphs.push(paragraphFromElement(child, {}));
    } else if (child.nodeType === Node.TEXT_NODE && child.textContent) {
      const text = cleanText(child.textContent);
      const last = paragraphs[paragraphs.length - 1];
      if (last) last.runs = normalizeRuns([...last.runs, { text }]);
      else paragraphs.push({ runs: [{ text }] });
    }
  });
  return paragraphs.length ? paragraphs : [{ runs: [{ text: '' }] }];
}

function paragraphElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.children) as HTMLElement[];
}

/** A DOM point inside the editable as a model position. */
export function domToPos(root: HTMLElement, node: Node, offset: number): TextPos | null {
  if (!root.contains(node)) return null;
  const paras = paragraphElements(root);
  if (paras.length === 0) return { p: 0, o: 0 };
  if (node === root) {
    if (offset >= paras.length) {
      const last = paras.length - 1;
      return { p: last, o: textLength(paras[last]!) };
    }
    return { p: offset, o: 0 };
  }
  let para: Node = node;
  while (para.parentNode && para.parentNode !== root) para = para.parentNode;
  const p = paras.indexOf(para as HTMLElement);
  if (p === -1) return null;
  const range = document.createRange();
  range.setStart(para, 0);
  range.setEnd(node, offset);
  let o = range.toString().split(ZWSP).join('').length;
  // The bullet glyph is text in the DOM but not in the model.
  const bullet = (para as HTMLElement).querySelector('[data-bullet]');
  if (bullet && range.intersectsNode(bullet) && !(node === para && offset === 0)) o -= 1;
  return { p, o: Math.max(0, o) };
}

function textLength(para: HTMLElement): number {
  let len = 0;
  walkText(para, (t) => {
    len += (t.textContent ?? '').length;
    return false;
  });
  return len;
}

/** Visits the text nodes of a paragraph in order, skipping the bullet; stops when fn returns true. */
function walkText(node: Node, fn: (t: Text) => boolean): boolean {
  for (const child of Array.from(node.childNodes)) {
    if (isBullet(child)) continue;
    if (child.nodeType === Node.TEXT_NODE) {
      if (fn(child as Text)) return true;
    } else if (walkText(child, fn)) return true;
  }
  return false;
}

/** A model position as a DOM point inside the editable. */
export function posToDom(root: HTMLElement, pos: TextPos): { node: Node; offset: number } {
  const paras = paragraphElements(root);
  const para = paras[Math.min(pos.p, paras.length - 1)];
  if (!para) return { node: root, offset: 0 };
  // Assigned inside the walk callback, so held in an object TypeScript doesn't narrow to null.
  const state: {
    remaining: number;
    found: { node: Node; offset: number } | null;
    lastText: Text | null;
  } = {
    remaining: pos.o,
    found: null,
    lastText: null,
  };
  walkText(para, (t) => {
    const len = (t.textContent ?? '').length;
    state.lastText = t;
    if (state.remaining <= len) {
      state.found = { node: t, offset: state.remaining };
      return true;
    }
    state.remaining -= len;
    return false;
  });
  if (state.found) return state.found;
  if (state.lastText)
    return { node: state.lastText, offset: state.lastText.textContent?.length ?? 0 };
  const span = para.querySelector('[data-run]');
  return span ? { node: span, offset: 0 } : { node: para, offset: para.childNodes.length };
}

export function setDomSelection(root: HTMLElement, start: TextPos, end: TextPos = start) {
  const sel = window.getSelection();
  if (!sel) return;
  const a = posToDom(root, start);
  const b = posToDom(root, end);
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  sel.removeAllRanges();
  sel.addRange(range);
}

/** The editable's current selection as model positions, or null if it isn't inside the root. */
export function readDomSelection(root: HTMLElement): { start: TextPos; end: TextPos } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const start = domToPos(root, range.startContainer, range.startOffset);
  const end = domToPos(root, range.endContainer, range.endOffset);
  return start && end ? { start, end } : null;
}
